import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listMarcadosConGps, type MarcadoGeo } from '../../api/pdvMapa';
import { resumirTienda } from '../../domain/pdvMapa';

// Leaflet y su CSS pesan más que toda esta pantalla junta, y el catálogo de tiendas se abre
// muchas veces solo para buscar un código. Se cargan al abrir el mapa, no antes: el jefe de
// operaciones entra a este panel desde su celular (§3.5 de la bitácora).
const MapaPdv = lazy(() => import('../../components/MapaPdv'));
import { parsePdvFile, type PdvImportRow } from '../../domain/excel/importPdv';
import {
  DEFAULT_PDV_RADIUS_M, bulkUpsertPdv, createPdv, getExistingPdvRadii, listPdv, setPdvActive, updatePdv,
  type PdvInput,
} from '../../api/pdv';
import { parseGoogleMapsUrl } from '../../domain/geo';
import type { LocationSource, PdvRow } from '../../lib/db-types';
import { puedeEditar } from '../../domain/permissions';
import { useAuth } from '../../components/AuthProvider';
import FilaTarjeta from '../../components/FilaTarjeta';
import TarjetasResumen from '../../components/TarjetasResumen';

interface FormState {
  id: string | null;
  code: string; name: string; chain: string; format: string;
  department: string; province: string; district: string; zone: string;
  address: string; lat: string; lng: string; radiusM: string;
  /** Texto pegado del enlace de Google Maps. No se guarda: solo alimenta lat/lng. */
  mapsUrl: string;
}

/** Etiqueta de procedencia de la coordenada. 'aproximada' es la que hay que ir a verificar:
 *  una coordenada mala genera alertas falsas de "lejos del PDV" en marcados legítimos. */
const ORIGEN_UBICACION: Record<LocationSource, { texto: string; clase: string }> = {
  aproximada: { texto: 'Sin verificar', clase: 'border-amber-300 bg-amber-50 text-amber-800' },
  terreno: { texto: 'Verificada en terreno', clase: 'border-green-300 bg-green-50 text-green-800' },
  mapa: { texto: 'Desde mapa', clase: 'border-gray-300 bg-gray-100 text-gray-700' },
};

function OrigenUbicacion({ source }: { source: LocationSource }) {
  const o = ORIGEN_UBICACION[source] ?? ORIGEN_UBICACION.aproximada;
  return (
    <span className={`inline-block rounded border px-1.5 py-0.5 text-xs ${o.clase}`}>{o.texto}</span>
  );
}

// Campos de texto libre del formulario (el radio se edita aparte, con su ayuda)
const TEXT_FIELDS: [keyof FormState, string][] = [
  ['code', 'Código'], ['name', 'Nombre'], ['chain', 'Cadena'], ['format', 'Formato'],
  ['department', 'Departamento'], ['province', 'Provincia'], ['district', 'Distrito'],
  ['zone', 'Zona'], ['address', 'Dirección'], ['lat', 'Lat'], ['lng', 'Lng'],
];

const EMPTY_FORM: FormState = {
  id: null, code: '', name: '', chain: '', format: '',
  department: '', province: '', district: '', zone: '',
  address: '', lat: '', lng: '', radiusM: String(DEFAULT_PDV_RADIUS_M), mapsUrl: '',
};

/** Aviso de geocerca: PDV que ya existe y cuyo radius_m cambiaría con esta importación. */
interface RadiusChange { code: string; from: number; to: number; }

interface ImportPreview {
  valid: PdvImportRow[];
  errors: { rowNumber: number; message: string }[];
  /** Filas con RadioM que pisarían un radio distinto ya guardado. */
  radiusChanges: RadiusChange[];
  /** PDV existentes cuya fila viene con RadioM vacío: conservan su radio actual. */
  radiusKept: number;
}
interface ImportReport { imported: number; failed: number; detail: string; }

export default function Pdv() {
  const { roles } = useAuth();
  // El catálogo lo LEEN el comercial, el coordinador y el supervisor (sin PDV no se entiende
  // la programación: cada posición es una tienda). Crearlo, editarlo, importarlo o
  // desactivarlo es del jefe de operaciones, y la RLS de `pdv` así lo exige: mostrar los
  // botones a quien va a recibir un 42501 solo enseña a desconfiar del sistema.
  const puedeEditarPdv = puedeEditar('pdv', roles);
  const [query, setQuery] = useState('');
  const [pdvs, setPdvs] = useState<PdvRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  // Enlace de mapa pegado en el formulario: si se leyó bien, la coordenada queda marcada
  // como 'mapa' al guardar (si no se pegó nada, la columna NO se toca).
  const [desdeMapa, setDesdeMapa] = useState(false);
  const [mapsMsg, setMapsMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [verMapa, setVerMapa] = useState(false);
  const [marcados, setMarcados] = useState<MarcadoGeo[]>([]);
  const [marcadosMsg, setMarcadosMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPdvs(await listPdv(query));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar PDV');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  // Los marcados se piden UNA vez, al abrir el mapa por primera vez. Si la lectura falla, el
  // mapa igual se dibuja con las tiendas y su geocerca: un error trayendo los puntos no puede
  // dejar sin mapa a quien entró a corregir la ubicación de una tienda.
  useEffect(() => {
    if (!verMapa || marcados.length > 0) return;
    let vivo = true;
    void (async () => {
      try {
        const { marcados: filas, hayMas } = await listMarcadosConGps();
        if (!vivo) return;
        setMarcados(filas);
        setMarcadosMsg(
          hayMas
            ? `Se muestran los ${filas.length} marcados más recientes con GPS; hay más en el histórico.`
            : null,
        );
      } catch {
        if (vivo) setMarcadosMsg('No se pudieron cargar los marcados. El mapa muestra solo las tiendas.');
      }
    })();
    return () => { vivo = false; };
  }, [verMapa, marcados.length]);

  const sinCoordenada = pdvs.filter((p) => p.lat === null || p.lng === null).length;

  const detalleMarcado = useCallback(
    (id: string) => {
      const m = marcados.find((x) => x.id === id);
      if (!m) return '';
      const hora = new Date(m.server_ts).toLocaleString('es-PE', { timeZone: 'America/Lima' });
      const alertas = m.flags.length > 0 ? `<br>Alertas: ${m.flags.join(', ')}` : '';
      const precision = m.accuracy === null ? '' : `<br>Precisión: ${Math.round(m.accuracy)} m`;
      return `<strong>${m.persona}</strong><br>${m.kind} · ${hora}${precision}${alertas}`;
    },
    [marcados],
  );

  // El resumen de fuera-del-radio es lo que convierte el mapa en una revisión y no en un
  // adorno: si una tienda acumula marcados fuera, la mal ubicada suele ser la TIENDA.
  const fueraDelRadio = useMemo(
    () => pdvs.reduce((total, p) => total + resumirTienda(p, marcados).fuera, 0),
    [pdvs, marcados],
  );

  // Un marcado de una tienda SIN coordenada no se puede dibujar (no hay contra qué ubicarlo).
  // Se cuentan aparte porque decir "30 marcados" mientras se ven 27 puntos es exactamente el
  // tipo de número que hace desconfiar de la pantalla entera.
  const conUbicacion = useMemo(
    () => new Set(pdvs.filter((p) => p.lat !== null && p.lng !== null).map((p) => p.id)),
    [pdvs],
  );
  const dibujados = marcados.filter((m) => conUbicacion.has(m.pdv_id)).length;
  const sinDibujar = marcados.length - dibujados;

  function openEdit(p: PdvRow | null) {
    setFormError(null);
    setDesdeMapa(false);
    setMapsMsg(null);
    if (!p) { setForm(EMPTY_FORM); return; }
    setForm({
      id: p.id, code: p.code, name: p.name, chain: p.chain ?? '', format: p.format ?? '',
      department: p.department ?? '', province: p.province ?? '',
      district: p.district ?? '', zone: p.zone ?? '', address: p.address ?? '',
      lat: p.lat === null ? '' : String(p.lat), lng: p.lng === null ? '' : String(p.lng),
      radiusM: String(p.radius_m), mapsUrl: '',
    });
  }

  /** Pegar el enlace de Google Maps rellena Lat y Lng; no se guarda el enlace en sí. */
  function onMapsUrlChange(texto: string) {
    if (!form) return;
    const parsed = parseGoogleMapsUrl(texto);
    if (parsed) {
      setForm({ ...form, mapsUrl: texto, lat: String(parsed.lat), lng: String(parsed.lng) });
      setDesdeMapa(true);
      setMapsMsg(`Coordenadas leídas del enlace: ${parsed.lat}, ${parsed.lng}`);
      return;
    }
    setForm({ ...form, mapsUrl: texto });
    setDesdeMapa(false);
    setMapsMsg(
      texto.trim() === ''
        ? null
        : 'No se encontraron coordenadas en ese texto. Si es un enlace corto (maps.app.goo.gl) ábrelo en Google Maps y copia el enlace largo, o pega directamente "-12.1268, -77.0287".',
    );
  }

  async function saveForm() {
    if (!form) return;
    if (form.code.trim() === '' || form.name.trim() === '') {
      setFormError('Código y Nombre son obligatorios'); return;
    }
    const lat = form.lat.trim() === '' ? null : Number(form.lat);
    const lng = form.lng.trim() === '' ? null : Number(form.lng);
    if ((lat !== null && !Number.isFinite(lat)) || (lng !== null && !Number.isFinite(lng))) {
      setFormError('Lat y Lng deben ser numéricos o vacíos'); return;
    }
    const radiusText = form.radiusM.trim();
    const radius = radiusText === '' ? DEFAULT_PDV_RADIUS_M : Number(radiusText);
    if (!Number.isInteger(radius) || radius <= 0) {
      setFormError('El radio de geocerca debe ser un número entero de metros mayor a 0'); return;
    }
    try {
      // Nuevo → crear (si el código ya existe, avisa y no toca nada); editar → por id. Antes era un upsert por código:
      // pegar un código existente en «Nuevo PDV» reescribía esa otra tienda sin aviso (16-oct, 0307).
      const guardar = form.id ? (r: PdvInput) => updatePdv(form.id as string, r) : createPdv;
      await guardar({
        code: form.code.trim(), name: form.name.trim(),
        chain: form.chain.trim() || null, format: form.format.trim() || null,
        department: form.department.trim() || null, province: form.province.trim() || null,
        district: form.district.trim() || null, zone: form.zone.trim() || null,
        address: form.address.trim() || null,
        lat, lng, radius_m: radius,
        // Solo se manda cuando la coordenada vino de un enlace de mapa: si la clave no
        // está, el upsert no pisa la marca de un PDV ya verificado en terreno.
        ...(desdeMapa ? { location_source: 'mapa' as const, location_set_at: new Date().toISOString() } : {}),
      });
      setForm(null);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Error al guardar');
    }
  }

  async function toggleActive(p: PdvRow) {
    await setPdvActive(p.id, !p.active);
    await load();
  }

  async function onFile(file: File) {
    setReport(null);
    setError(null);
    const parsed = parsePdvFile(await file.arrayBuffer());
    // Aviso de geocerca: comparar el RadioM del archivo contra el radius_m ya guardado
    let radiusChanges: RadiusChange[] = [];
    let radiusKept = 0;
    try {
      const existing = await getExistingPdvRadii(parsed.rows.map((r) => r.code));
      for (const r of parsed.rows) {
        const current = existing.get(r.code);
        if (current === undefined) continue;            // PDV nuevo: no hay radio que pisar
        if (r.radiusM === null) { radiusKept += 1; continue; }
        if (r.radiusM !== current) radiusChanges.push({ code: r.code, from: current, to: r.radiusM });
      }
    } catch {
      radiusChanges = [];
      radiusKept = 0;
      setError('No se pudo verificar los radios de geocerca actuales; revisa el detalle después de importar.');
    }
    setPreview({ valid: parsed.rows, errors: parsed.errors, radiusChanges, radiusKept });
  }

  /** Columnas comunes a todas las filas, SIN el radio de geocerca. */
  function baseInput(r: PdvImportRow): PdvInput {
    return {
      code: r.code, name: r.name,
      chain: r.chain || null, format: r.format || null,
      department: r.department || null, province: r.province || null,
      district: r.district || null, zone: r.zone || null,
      address: r.address || null,
      lat: r.lat, lng: r.lng,
    };
  }

  async function confirmImport() {
    if (!preview) return;
    setImporting(true);
    try {
      // DOS PASADAS. Las filas con RadioM vacío se envían SIN la clave `radius_m`, para que el
      // upsert no devuelva a 300 m un PDV al que le habían puesto 500 (el default de la tabla
      // solo aplica cuando el PDV es nuevo). Van en llamadas separadas porque PostgREST exige
      // que todas las filas de un mismo lote tengan exactamente las mismas claves.
      const sinRadio: PdvInput[] = preview.valid
        .filter((r) => r.radiusM === null)
        .map(baseInput);
      const conRadio: PdvInput[] = preview.valid
        .filter((r) => r.radiusM !== null)
        .map((r) => ({ ...baseInput(r), radius_m: r.radiusM as number }));
      const imported = (await bulkUpsertPdv(sinRadio)) + (await bulkUpsertPdv(conRadio));
      const lines = [
        `Importación de PDV — ${new Date().toLocaleString('es-PE')}`,
        `Importados: ${imported}`,
        `Errores: ${preview.errors.length}`,
        `Radios de geocerca modificados: ${preview.radiusChanges.length}`,
        `PDV existentes que conservan su radio (RadioM vacío): ${preview.radiusKept}`,
        '',
        ...preview.errors.map((e) => `Fila ${e.rowNumber}: ${e.message}`),
        ...(preview.radiusChanges.length > 0 ? [''] : []),
        ...preview.radiusChanges.map((c) => `PDV ${c.code}: radio ${c.from} m → ${c.to} m`),
      ];
      setReport({ imported, failed: preview.errors.length, detail: lines.join('\n') });
      setPreview(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al importar');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function downloadDetail() {
    if (!report) return;
    const url = URL.createObjectURL(new Blob([report.detail], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'importacion-pdv.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-xl font-bold">Puntos de venta</h1>
        {puedeEditarPdv ? (
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-2 bg-gray-200 rounded" onClick={() => fileRef.current?.click()}>
              Importar Excel
            </button>
            <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => openEdit(null)}>
              Nuevo PDV
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Catálogo de consulta. Crear o corregir un PDV es del jefe de operaciones.
          </p>
        )}
      </div>
      <input
        ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
      />
      <input
        className="border rounded px-3 py-2 w-full mb-4"
        placeholder="Buscar por código, nombre o distrito…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {error && <p className="text-red-600 mb-2">{error}</p>}

      {/* `pdvs` ya viene filtrado por la búsqueda —el filtro es del servidor—, así que las tarjetas
          hablan de lo que está en la tabla y no del catálogo entero.

          «Sin coordenada» va con tono de atención por una razón concreta que ya está anotada en las
          instrucciones del proyecto: una tienda sin coordenada NO evalúa geocerca, y su silencio se
          confunde con «todo en orden». La de «fuera del radio» aparece solo cuando el mapa cargó
          los marcados: sin ellos el número sería cero y se leería como que no hay problema. */}
      {!loading && pdvs.length > 0 && (
        <TarjetasResumen indicadores={[
          { titulo: 'Tiendas', valor: pdvs.length,
            detalle: query.trim() === '' ? undefined : 'con la búsqueda puesta' },
          { titulo: 'Sin coordenada', valor: sinCoordenada, tono: 'atencion',
            detalle: 'ahí no se evalúa la geocerca' },
          { titulo: 'Inactivas', valor: pdvs.filter((x) => !x.active).length, tono: 'atencion' },
          { titulo: 'Cadenas',
            valor: new Set(pdvs.map((x) => x.chain ?? '').filter((c) => c !== '')).size },
          { titulo: 'Distritos',
            valor: new Set(pdvs.map((x) => x.district ?? '').filter((d) => d !== '')).size },
          marcados.length === 0 ? null
            : { titulo: 'Marcados fuera del radio', valor: fueraDelRadio, tono: 'malo',
                detalle: `de ${dibujados} dibujados` },
        ]} />
      )}

      <div className="mb-4 rounded border border-slate-300 bg-slate-50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            className="rounded bg-slate-200 px-3 py-2 text-sm font-medium"
            onClick={() => setVerMapa((v) => !v)}
            aria-expanded={verMapa}
          >
            {verMapa ? 'Ocultar mapa' : 'Ver mapa'}
          </button>
          <p className="text-sm text-slate-700">
            {pdvs.length - sinCoordenada} de {pdvs.length} tienda(s) con coordenada
            {/* Decirlo siempre, y no solo dentro del mapa: una tienda sin coordenada NO evalúa
                geocerca, y su silencio se confunde con "todo en orden". */}
            {sinCoordenada > 0 && (
              <span className="text-amber-800">
                {' '}· {sinCoordenada} sin ubicar, y ahí la geocerca no se evalúa
              </span>
            )}
            {verMapa && marcados.length > 0 && (
              <span> · {dibujados} marcado(s) en el mapa</span>
            )}
            {verMapa && sinDibujar > 0 && (
              <span className="text-amber-800">
                {' '}· {sinDibujar} no se puede(n) dibujar: su tienda no tiene coordenada
              </span>
            )}
            {verMapa && fueraDelRadio > 0 && (
              <span className="text-red-700"> · {fueraDelRadio} fuera del radio</span>
            )}
          </p>
        </div>
        {verMapa && (
          <div className="mt-3">
            {marcadosMsg && <p className="mb-2 text-sm text-amber-800">{marcadosMsg}</p>}
            <Suspense fallback={<p className="text-sm text-slate-600">Cargando el mapa…</p>}>
              <MapaPdv tiendas={pdvs} marcados={marcados} detalleMarcado={detalleMarcado} />
            </Suspense>
          </div>
        )}
      </div>

      {preview && (
        <div className="border rounded p-4 mb-4 bg-yellow-50">
          <h2 className="font-semibold mb-2">Previsualización de importación</h2>
          <p className="mb-2">{preview.valid.length} filas válidas · {preview.errors.length} filas con error</p>
          {preview.errors.length > 0 && (
            <ul className="text-sm text-red-700 mb-2 list-disc pl-5">
              {preview.errors.map((e) => <li key={e.rowNumber}>Fila {e.rowNumber}: {e.message}</li>)}
            </ul>
          )}
          {preview.radiusChanges.length > 0 && (
            <div className="text-sm mb-2 border border-orange-300 bg-orange-50 rounded p-2">
              <p className="font-semibold text-orange-800">
                {preview.radiusChanges.length} PDV ya existentes cambiarían su radio de geocerca:
              </p>
              <ul className="list-disc pl-5 text-orange-900">
                {preview.radiusChanges.map((c) => (
                  <li key={c.code}>{c.code}: {c.from} m → {c.to} m</li>
                ))}
              </ul>
            </div>
          )}
          {preview.radiusKept > 0 && (
            <p className="text-sm text-gray-700 mb-2">
              {preview.radiusKept} PDV existentes traen la columna RadioM vacía: conservan el radio
              que ya tienen configurado.
            </p>
          )}
          <div className="flex gap-2">
            <button
              className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
              disabled={importing || preview.valid.length === 0}
              onClick={() => void confirmImport()}
            >
              {importing ? 'Importando…' : `Importar ${preview.valid.length} PDV`}
            </button>
            <button className="px-3 py-2 bg-gray-200 rounded" onClick={() => setPreview(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {report && (
        <div className="border rounded p-4 mb-4 bg-green-50 flex flex-wrap items-center justify-between gap-2">
          <p>{report.imported} importados, {report.failed} errores</p>
          <button className="px-3 py-2 bg-gray-200 rounded" onClick={downloadDetail}>
            Descargar detalle (.txt)
          </button>
        </div>
      )}

      {/* En celular: una tarjeta por PDV (sin desplazamiento horizontal). */}
      <div className="space-y-2 lg:hidden">
        {loading && <p className="border rounded p-4 text-center text-gray-500">Cargando…</p>}
        {!loading && pdvs.length === 0 && (
          <p className="border rounded p-4 text-center text-gray-500">Sin resultados</p>
        )}
        {pdvs.map((p) => (
          <FilaTarjeta
            key={p.id}
            titulo={p.name}
            estados={
              <>
                <span className={p.active ? 'text-green-700' : 'text-gray-500'}>
                  {p.active ? 'Activo' : 'Inactivo'}
                </span>
                <OrigenUbicacion source={p.location_source} />
              </>
            }
            campos={[
              { etiqueta: 'Código', valor: p.code },
              { etiqueta: 'Cadena', valor: p.chain },
              { etiqueta: 'Distrito', valor: p.district },
              { etiqueta: 'Radio', valor: `${p.radius_m} m` },
            ]}
            acciones={
              puedeEditarPdv ? (
                <>
                  <button className="text-blue-600 underline" onClick={() => openEdit(p)}>Editar</button>
                  <button className="text-gray-600 underline" onClick={() => void toggleActive(p)}>
                    {p.active ? 'Desactivar' : 'Activar'}
                  </button>
                </>
              ) : null
            }
          />
        ))}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2">Código</th><th className="p-2">Nombre</th><th className="p-2">Cadena</th>
              <th className="p-2">Distrito</th><th className="p-2">Radio</th>
              <th className="p-2">Estado</th>
              {puedeEditarPdv && <th className="p-2">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="p-2" colSpan={7}>Cargando…</td></tr>}
            {!loading && pdvs.length === 0 && <tr><td className="p-2" colSpan={7}>Sin resultados</td></tr>}
            {pdvs.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="p-2">{p.code}</td>
                <td className="p-2">{p.name}</td>
                <td className="p-2">{p.chain ?? ''}</td>
                <td className="p-2">{p.district ?? ''}</td>
                <td className="p-2">{p.radius_m} m</td>
                <td className="p-2">
                  <span className={p.active ? 'text-green-700' : 'text-gray-500'}>
                    {p.active ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="block mt-1"><OrigenUbicacion source={p.location_source} /></span>
                </td>
                {puedeEditarPdv && (
                  <td className="p-2 flex gap-2">
                    <button className="text-blue-600 underline" onClick={() => openEdit(p)}>Editar</button>
                    <button className="text-gray-600 underline" onClick={() => void toggleActive(p)}>
                      {p.active ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded p-4 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="font-semibold mb-3">{form.id ? 'Editar PDV' : 'Nuevo PDV'}</h2>
            <div className="grid grid-cols-2 gap-3">
              {TEXT_FIELDS.map(([key, label]) => (
                <label key={String(key)} className="text-sm">
                  {label}
                  <input
                    className="border rounded px-2 py-1 w-full"
                    value={String(form[key] ?? '')}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                </label>
              ))}
              <label className="text-sm col-span-2">
                Enlace de Google Maps
                <input
                  className="border rounded px-2 py-1 w-full"
                  placeholder="Pega el enlace o -12.1268, -77.0287"
                  value={form.mapsUrl}
                  onChange={(e) => onMapsUrlChange(e.target.value)}
                />
                {mapsMsg ? (
                  <span className={`block text-xs mt-1 ${desdeMapa ? 'text-green-700' : 'text-orange-700'}`}>
                    {mapsMsg}
                  </span>
                ) : (
                  <span className="block text-xs text-gray-600 mt-1">
                    Opcional: rellena Lat y Lng automáticamente. En Google Maps toca "Compartir →
                    Copiar vínculo" sobre la tienda y pégalo aquí.
                  </span>
                )}
              </label>
              <label className="text-sm col-span-2">
                Radio de geocerca (metros)
                <input
                  className="border rounded px-2 py-1 w-full"
                  inputMode="numeric"
                  value={form.radiusM}
                  placeholder={String(DEFAULT_PDV_RADIUS_M)}
                  onChange={(e) => setForm({ ...form, radiusM: e.target.value })}
                />
                <span className="block text-xs text-gray-600 mt-1">
                  Distancia máxima permitida al marcar. Por defecto {DEFAULT_PDV_RADIUS_M} m;
                  súbelo en tiendas grandes o con GPS malo (sótanos, centros comerciales).
                </span>
              </label>
            </div>
            {formError && <p className="text-red-600 mt-2">{formError}</p>}
            <div className="flex gap-2 mt-4 justify-end">
              <button className="px-3 py-2 bg-gray-200 rounded" onClick={() => setForm(null)}>Cancelar</button>
              <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => void saveForm()}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
